import { yupResolver } from '@hookform/resolvers/yup';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import React, { SyntheticEvent, useContext, useEffect, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import LocalStorageHelper from '../../adapters/helpers/localStorageHelper';
import {
  analyticsService,
  GtagCouponCodeValues,
} from '../../application/analytics';
import { billingService } from '../../application/billing';
import { userService } from '../../application/user';
import Alert from '../../components/common/alert';
import Button from '../../components/common/button';
import LoadingSpinner from '../../components/common/loadingSpinner';
import Checkbox from '../../components/forms/checkbox';
import FormContainer from '../../components/forms/formContainer';
import Input from '../../components/forms/input';
import Layout from '../../components/layout/layout';
import { AppContext, Types } from '../../context';
import { routes } from '../../domain/websiteDomain';
import { passwordValidation } from '../../libs/text';

interface CreateUserProps {}

interface Values {
  name: string;
  email: string;
  password: string;
  acceptTerms: boolean;
  firstName: string;
  lastName: string;
}

interface CouponCodeResponse {
  message: string;
  subMessage: string;
  valid: boolean;
  CCNotRequired: boolean;
  days: string;
}

const schema = Yup.object({
  firstName: Yup.string().required('required'),
  lastName: Yup.string().required('required'),
  email: Yup.string().email('must be a valid email').required('required'),
  password: passwordValidation,
  acceptTerms: Yup.boolean().oneOf(
    [true],
    'Please accept terms and conditions before continuing'
  ),
});

const CreateUser: React.FC<CreateUserProps> = () => {
  const apiKey = process.env.NEXT_PUBLIC_RECAPTCHA_KEY || '';

  const router = useRouter();
  const { state, dispatch } = useContext(AppContext);
  const [alert, setAlert] = useState<string | null>(null);
  const [promoCode, setPromoCode] = useState<string>(
    (router?.query?.coupon as string) ?? ''
  );
  const [promoCodeStatus, setPromoCodeStatus] = useState<
    'error' | 'success' | 'submitting' | 'notification' | null
  >(null);
  const [promoCodeData, setPromoCodeData] = useState<CouponCodeResponse | null>(
    null
  );

  const [promoCodeStatusMessage, setPromoCodeStatusMessage] = useState<
    string | null
  >(null);
  const [success, setSuccess] = useState<boolean>(false);
  const [recaptcha, setRecaptcha] = useState<string>('');

  const { register, handleSubmit, errors, formState, getValues, trigger } =
    useForm<Values>({
      resolver: yupResolver(schema),
      mode: 'onTouched',
    });

  useEffect(() => {
    if (router.query.coupon) {
      setPromoCode(router.query.coupon as string);
      setPromoCodeStatus('notification');
      setPromoCodeStatusMessage(
        "Please enter your email address and click 'Apply coupon'"
      );
    }
  }, [router.query]);

  const handleApplyCouponCode = async (e: SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const applicationBilling = billingService();
    const storageHelper = LocalStorageHelper.getInstance();

    setPromoCodeStatusMessage(null);
    setPromoCodeStatus('submitting');

    const emailValid = await trigger('email');

    // Check that the email field is set and that it's valid before continuing
    if (!emailValid || errors.email) {
      setPromoCodeStatus('error');
      setPromoCodeStatusMessage(
        'Please enter your email before applying a promo code / gift card code'
      );
      return;
    }

    // User hasn't typed anything or is a bunch of spaces
    if (typeof promoCode !== 'string' || !promoCode.trim()) {
      setPromoCodeStatus('error');
      setPromoCodeStatusMessage('Please enter a promo code / gift card code');
      return;
    }

    const email = getValues('email');
    const analytics = analyticsService();
    try {
      const res = await applicationBilling.checkPromoCode(promoCode, email);
      if (res?.valid) {
        analytics.recordCouponCode(
          promoCode,
          email,
          GtagCouponCodeValues.VALID_CODE
        );

        // Add the code to local storage in case the user is pushed to the subscribe page
        // because the code is less than 100% off and still requires a credit card
        // where it can be picked up and handled like query-param based codes pushed from social media etc.
        storageHelper.setCouponData(promoCode.trim());
        setPromoCodeStatus('success');
        setPromoCodeStatusMessage(
          res.subMessage ||
            `Promo code / gift card code ${promoCode} successfully applied!`
        );
      } else {
        analytics.recordCouponCode(
          promoCode,
          email,
          GtagCouponCodeValues.INVALID_CODE
        );
        setPromoCodeStatus('error');
        setPromoCodeStatusMessage(
          res.subMessage || `Promo code / gift card code not valid`
        );
      }
      setPromoCodeData(res);
    } catch (err) {
      analytics.recordCouponCode(
        promoCode,
        email,
        GtagCouponCodeValues.FAILED_REQUEST
      );
      setPromoCodeStatus('error');
      setPromoCodeStatusMessage(
        'Something went wrong applying the promo code / gift card code'
      );
    }
  };

  const onCaptureChange = async (value: any) => {
    setRecaptcha(value);
  };

  const onSubmit = handleSubmit(async (values: Values): Promise<void> => {
    const user = userService();
    const analytics = analyticsService();

    if (!recaptcha) {
      // short circuit if the captcha token has not been set
      setAlert('Please complete the reCAPTCHA before continuing');
      return;
    }

    // Scroll to the top of the page so that the loading message is in view
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });

    const email = values.email.toLowerCase();
    const { password, firstName, lastName } = values;

    try {
      const newUser = await user.signupUser({
        acceptTerms: values.acceptTerms,
        email,
        confirm_password: password,
        password: password,
        promoCode: promoCodeData?.valid ? promoCode : null,
        referral: 'BML',
        type: 'Client',
        firstName,
        lastName,
        recaptcha: recaptcha,
      });

      dispatch({ type: Types.SET_USER, payload: newUser });

      analytics.recordUserAccountCreation(newUser);

      // if signup is successful then login and redirect to homepage
      setSuccess(true);

      //if onboarding flow was completed then save this to the user's data store
      const { onboarding } = state.settings;
      if (onboarding) {
        await user.saveUserOnboardingData(onboarding);
      }

      if (promoCodeData?.CCNotRequired) {
        router.push('/');
      } else {
        router.push('/user/subscribe');
      }
    } catch (err) {
      setAlert((err as Error).message);
    }
  });

  const loadingPanel = () => (
    <div data-cy="signup-loading-section" className="h-screen text-center">
      <FormContainer>
        <div className="pb-6 text-2xl font-bold md:text-3xl">
          Thanks for creating your account.
        </div>
        <p className="pb-2">We are directing you to the payment page</p>
        <p className="pb-6">Hold on, do not refresh.</p>
        <LoadingSpinner />
      </FormContainer>
    </div>
  );

  const signupForm = () => (
    <FormContainer
      title={
        promoCodeData?.message
          ? promoCodeData.message
          : `Sign up and start your free 7 day trial`
      }
    >
      <div className="pb-8">
        <p>
          We believe in building a community where students are supported and
          teachers are rewarded. For every class you take, your teacher gets
          paid - even when you{"'"}re on a free trial!
        </p>
      </div>
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            name="firstName"
            label="First Name"
            ref={register}
            errors={errors}
          />
          <Input
            name="lastName"
            label="Last Name"
            ref={register}
            errors={errors}
          />
        </div>
        <Input
          label="Email"
          type="email"
          name="email"
          ref={register}
          errors={errors}
        />
        <Input
          label="Password"
          type="password"
          name="password"
          ref={register}
          errors={errors}
        />

        <div className="space-y-4 sm:flex sm:space-y-0 sm:space-x-4 sm:justify-between">
          <div className="w-full">
            <Input
              label="Promo Code / Gift Card Code (Optional)"
              type="text"
              name="promoCode"
              disabled={promoCodeStatus === 'success'}
              value={promoCode}
              onChange={e => setPromoCode(e.target.value.toUpperCase())}
              errors={
                promoCodeStatus === 'error'
                  ? { promoCode: { message: `${promoCodeStatusMessage}` } }
                  : {}
              }
            />
          </div>
          <div
            className={clsx(
              'sm:w-1/2 sm:pt-8',
              promoCodeStatus === 'success' && 'opacity-20'
            )}
          >
            <Button
              buttonStyle="secondary"
              classes={clsx(
                'w-full',
                promoCodeStatus === 'success' && 'pointer-events-none'
              )}
              onClick={handleApplyCouponCode}
              isDisabled={promoCodeStatus === 'success'}
              isSubmitting={promoCodeStatus === 'submitting'}
            >
              Apply coupon
            </Button>
          </div>
        </div>
        {(promoCodeStatus === 'success' ||
          promoCodeStatus === 'notification') && (
          <div className="p-4 text-center bg-gray-100 border rounded-lg border-input-background">
            <p className="font-family-bold">
              <span data-cy="coupon-code-submessage" className="p-0">
                {promoCodeStatusMessage}
              </span>
            </p>
          </div>
        )}

        <Checkbox
          name="acceptTerms"
          ref={register()}
          errors={errors}
          label={`I accept the <a class="text-accent" href="${routes.TERMS_AND_CONDITIONS}" target="_blank" rel="noopener noreferrer">terms and conditions</a>, <a class="text-accent" href="${routes.TERMS_AND_CONDITIONS}" target="_blank" rel="noopener noreferrer">injury waiver</a> and the <a class="text-accent" href="${routes.PRIVACY_POLICY}" target="_blank" rel="noopener noreferrer">privacy policy</a>`}
          onClick={() => trigger('acceptTerms')}
        />

        {alert && <Alert status="error">{alert}</Alert>}

        {!apiKey ? (
          <LoadingSpinner />
        ) : (
          <div data-cy="recaptcha-container">
            <ReCAPTCHA sitekey={apiKey} onChange={onCaptureChange} />
          </div>
        )}

        <div className="pt-6">
          <Button
            buttonStyle="primary"
            type="submit"
            classes="w-full"
            isSubmitting={formState.isSubmitting || success}
            isDisabled={
              formState.isSubmitting ||
              promoCodeStatus === 'submitting' ||
              !recaptcha ||
              !formState.isValid
            }
          >
            Sign Up
          </Button>
        </div>
      </form>
    </FormContainer>
  );

  return (
    <Layout classes="bg-background-lighter" title="Get Started">
      {formState.isSubmitting || success ? loadingPanel() : signupForm()}
    </Layout>
  );
};

export default CreateUser;
